import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  TvButton,
  TvCol,
  TvFormGroup,
  TvInput,
  TvLabel,
  TvRow,
  TvDiv,
  TvH6,
  TvP,
  TvNav,
  TvNavItem,
  TvNavLink,
  TvSmall,
} from "@trustvid/design-system";
import {
  estimateItineraryDraftLoadingSeconds,
  resolveItineraryLoadingStage,
} from "../../../../utils/itineraryDraftLoadingEstimate";
import { CREATE_DESIGN_ITINERARY_LABEL } from "../../../../constants/saasPlansCatalog";

const INPUT_MODES = [
  { key: "pdf", label: "PDF Upload" },
  { key: "prompt", label: "Enter a prompt" },
];
const MEDIA_MODES = [
  { key: "image", label: "Image" },
  { key: "video", label: "Video" },
];

const ItineraryDraftPanel = ({
  aspectRatio = "16:9",
  onAspectRatioChange = () => {},
  campaignTitle = "",
  onCampaignTitleChange = () => {},
  onGenerateItineraryDraft = () => {},
  isGeneratingItineraryDraft = false,
}) => {
  const fileInputRef = useRef(null);
  const lastVoiceToggleRef = useRef({ next: true, at: 0 });
  const [inputMode, setInputMode] = useState("pdf");
  const [selectedFile, setSelectedFile] = useState(null);
  const [emailText, setEmailText] = useState("");
  const [fileError, setFileError] = useState("");
  const [promptError, setPromptError] = useState("");
  const [mediaMode, setMediaMode] = useState("image");
  const [usePersonalizedVoiceover, setUsePersonalizedVoiceover] = useState(true);
  const [loadingSeconds, setLoadingSeconds] = useState(0);
  const [loadingEstimateSeconds, setLoadingEstimateSeconds] = useState(45);

  useEffect(() => {
    if (!isGeneratingItineraryDraft) {
      setLoadingSeconds(0);
      setLoadingEstimateSeconds(45);
      return undefined;
    }
    const intervalId = setInterval(() => {
      setLoadingSeconds((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(intervalId);
  }, [isGeneratingItineraryDraft]);

  const handleFileChange = (event) => {
    const file = event?.target?.files?.[0] || null;
    setFileError("");
    if (!file) {
      setSelectedFile(null);
      return;
    }

    const lowerName = String(file.name || "").toLowerCase();
    const isPdf = file.type === "application/pdf" || lowerName.endsWith(".pdf");
    const isEml = file.type === "message/rfc822" || lowerName.endsWith(".eml");

    if (!isPdf && !isEml) {
      setSelectedFile(null);
      setFileError("Please upload a PDF or .eml booking confirmation file.");
      return;
    }

    setSelectedFile(file);
  };

  const canGenerate = !isGeneratingItineraryDraft && aspectRatio;

  const validateBeforeGenerate = () => {
    let isValid = true;
    setFileError("");
    setPromptError("");

    if (inputMode === "pdf") {
      if (!selectedFile) {
        setFileError("Trip PDF or .eml file is required.");
        isValid = false;
      }
    }

    if (inputMode === "prompt") {
      const trimmedPrompt = emailText.trim();
      if (!trimmedPrompt) {
        setPromptError("Please enter itinerary details to generate the draft.");
        isValid = false;
      } else if (trimmedPrompt.length < 20) {
        setPromptError("Prompt is too short. Please add more itinerary details.");
        isValid = false;
      }
    }

    return isValid;
  };

  const applyVoiceoverToggle = (nextChecked) => {
    const next = Boolean(nextChecked);
    const now = typeof performance !== "undefined" ? performance.now() : Date.now();
    const prev = lastVoiceToggleRef.current;
    // Guard against duplicate onChange/onClick in modal event bubbling.
    if (prev.next === next && now - prev.at < 4) {
      return;
    }
    lastVoiceToggleRef.current = { next, at: now };
    setUsePersonalizedVoiceover(next);
  };

  const handleGenerate = () => {
    if (!validateBeforeGenerate() || !canGenerate) {
      return;
    }
    setLoadingEstimateSeconds(estimateItineraryDraftLoadingSeconds({
      mediaMode,
      usePersonalizedVoiceover,
      inputMode,
      emailText: inputMode === "prompt" ? emailText.trim() : "",
      campaignTitle: campaignTitle.trim(),
      fileName: selectedFile?.name || "",
    }));
    onGenerateItineraryDraft({
      inputMode,
      file: inputMode === "pdf" ? selectedFile : null,
      emailText: inputMode === "prompt" ? emailText.trim() : "",
      aspectRatio,
      title: campaignTitle.trim(),
      mediaMode,
      usePersonalizedVoiceover,
    });
  };

  return (
    <TvDiv className="mt-2 border rounded p-3" data-testid="itinerary-draft-panel">
      <TvH6 className="mb-1">{CREATE_DESIGN_ITINERARY_LABEL}</TvH6>
      <TvP className="text-muted mb-3">
        Start from a trip PDF or a prompt. We generate a travel itinerary video with an intro slate and day-by-day scenes. Other document types are not supported.
      </TvP>

      <TvNav pills className="mb-3 gap-1">
        {INPUT_MODES.map((mode) => (
          <TvNavItem key={mode.key}>
            <TvNavLink
              href="#"
              active={inputMode === mode.key}
              data-testid={`itinerary-mode-${mode.key}`}
              onClick={(event) => {
                event.preventDefault();
                setInputMode(mode.key);
                setFileError("");
                setPromptError("");
              }}
            >
              {mode.label}
            </TvNavLink>
          </TvNavItem>
        ))}
      </TvNav>

      {inputMode === "pdf" ? (
        <TvFormGroup className="mb-3">
          <TvLabel for="itinerary-pdf-upload">Trip PDF or .eml file</TvLabel>
          <TvInput
            id="itinerary-pdf-upload"
            innerRef={fileInputRef}
            type="file"
            accept=".pdf,.eml,application/pdf,message/rfc822"
            onChange={handleFileChange}
            data-testid="itinerary-pdf-upload"
          />
          {selectedFile ? (
            <TvSmall className="text-muted d-block mt-1">{selectedFile.name}</TvSmall>
          ) : null}
          {fileError ? (
            <TvSmall className="text-danger d-block mt-1">{fileError}</TvSmall>
          ) : null}
        </TvFormGroup>
      ) : (
        <TvFormGroup className="mb-3">
          <TvLabel for="itinerary-prompt-textarea">Enter a prompt</TvLabel>
          <TvInput
            id="itinerary-prompt-textarea"
            type="textarea"
            rows={8}
            value={emailText}
            onChange={(event) => setEmailText(event.target.value)}
            placeholder="Enter itinerary details: client name, travel dates, destinations, and day-by-day activities (or paste a booking confirmation email)."
            data-testid="itinerary-prompt-textarea"
          />
          {promptError ? (
            <TvSmall className="text-danger d-block mt-1" data-testid="itinerary-prompt-error">
              {promptError}
            </TvSmall>
          ) : null}
        </TvFormGroup>
      )}

      <TvRow className="g-3 mb-3">
        <TvCol md={6}>
          <TvFormGroup className="mb-0">
            <TvLabel for="itinerary-aspect-ratio">Aspect Ratio</TvLabel>
            <TvInput
              id="itinerary-aspect-ratio"
              type="select"
              value={aspectRatio}
              onChange={(event) => onAspectRatioChange(event.target.value)}
              data-testid="itinerary-aspect-ratio"
            >
              <option value="16:9">16:9 Landscape</option>
              <option value="9:16">9:16 Portrait</option>
              <option value="1:1">1:1 Square</option>
            </TvInput>
          </TvFormGroup>
        </TvCol>
        <TvCol md={6}>
          <TvFormGroup className="mb-0">
            <TvLabel for="itinerary-media-mode">Media Mode</TvLabel>
            <TvInput
              id="itinerary-media-mode"
              type="select"
              value={mediaMode}
              onChange={(event) => setMediaMode(event.target.value)}
              data-testid="itinerary-media-mode"
            >
              {MEDIA_MODES.map((mode) => (
                <option key={mode.key} value={mode.key}>
                  {mode.label}
                </option>
              ))}
            </TvInput>
          </TvFormGroup>
        </TvCol>
      </TvRow>

      <TvRow className="g-3 mb-3">
        <TvCol md={6}>
          <TvFormGroup className="mb-0">
            <TvLabel for="itinerary-campaign-title">Campaign title (optional)</TvLabel>
            <TvInput
              id="itinerary-campaign-title"
              value={campaignTitle}
              onChange={(event) => onCampaignTitleChange(event.target.value)}
              placeholder="Auto-detected from itinerary"
              data-testid="itinerary-campaign-title"
            />
          </TvFormGroup>
        </TvCol>
        <TvCol md={6}>
          <TvFormGroup className="mb-0 mt-4">
            <TvLabel className="d-block mb-1">
              Personalized Voiceover
            </TvLabel>
            <TvFormGroup check className="form-switch mt-2 mb-0">
              <TvInput
                id="itinerary-personalized-voiceover"
                type="switch"
                checked={usePersonalizedVoiceover}
                onChange={(event) => applyVoiceoverToggle(event.target.checked)}
                onClick={() => applyVoiceoverToggle(!usePersonalizedVoiceover)}
                data-testid="itinerary-personalized-voiceover-toggle"
              />
              <TvLabel
                check
                for="itinerary-personalized-voiceover"
                data-testid="itinerary-personalized-voiceover-label"
              >
                Enable personalized narration audio
              </TvLabel>
            </TvFormGroup>
            <TvSmall
              className="text-muted d-block mt-1"
              data-testid="itinerary-voiceover-toggle-status"
            >
              {usePersonalizedVoiceover
                ? "Voiceover enabled"
                : "Voiceover disabled (text overlays only)"}
            </TvSmall>
          </TvFormGroup>
        </TvCol>
      </TvRow>

      <TvDiv className="d-flex justify-content-end">
        <TvButton
          color="primary"
          onClick={handleGenerate}
          disabled={!canGenerate}
          data-testid="itinerary-generate-draft"
        >
          {isGeneratingItineraryDraft ? "Generating..." : "Generate Itinerary Draft"}
        </TvButton>
      </TvDiv>
      {isGeneratingItineraryDraft ? (
        <TvDiv className="mt-2 text-end" data-testid="itinerary-loading-status">
          <TvSmall className="d-block text-muted">
            {resolveItineraryLoadingStage(loadingSeconds, loadingEstimateSeconds)}
          </TvSmall>
          <TvSmall className="d-block text-muted">
            Elapsed {loadingSeconds}s • Typical {loadingEstimateSeconds}s
          </TvSmall>
        </TvDiv>
      ) : null}
    </TvDiv>
  );
};

ItineraryDraftPanel.propTypes = {
  aspectRatio: PropTypes.string,
  onAspectRatioChange: PropTypes.func,
  campaignTitle: PropTypes.string,
  onCampaignTitleChange: PropTypes.func,
  onGenerateItineraryDraft: PropTypes.func,
  isGeneratingItineraryDraft: PropTypes.bool,
};

export default ItineraryDraftPanel;
